import React, { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";

import { GET_USER_QUALITATIVE_BENEFITS_BY_ID } from "../../apollo-queries";
import { UPDATE_QUALITATIVE_BENEFITS_BY_USER_ID } from "../../apollo-mutations";
import { useMutation, useQuery } from "@apollo/client";

import { NavigationText, QualitativeText } from "../../Text";
import { CiCircleInfo } from "react-icons/ci";
import { Button } from "@mui/base";
import { FaTrash } from "react-icons/fa";
import { Tooltip } from "@mui/material";
import { MdAddCircleOutline, MdAddCircle, MdClose } from "react-icons/md";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";

interface QualitativeBenefits {
  category: string;
  benefits: string[];
}

const QualitativeForm: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const {
    loading: fetchLoading,
    error: fetchError,
    data: databaseData,
    refetch,
  } = useQuery(GET_USER_QUALITATIVE_BENEFITS_BY_ID, {
    variables: { user_id: user.uuid },
  });
  const [
    updateQualitativeBenefits,
    { loading: updateLoading, error: updateError },
  ] = useMutation(UPDATE_QUALITATIVE_BENEFITS_BY_USER_ID);

  // format the data from the database to match the form data and make TS happy
  const formattedDatabaseData = React.useMemo<QualitativeBenefits[]>(() => {
    if (databaseData) {
      return databaseData.qualitative_benefits.map((item) => {
        return {
          category: item.category,
          benefits: item.benefits,
        };
      });
    } else {
      return [];
    }
  }, [databaseData]);

  // track the form data
  const { register, control, watch, setValue } = useForm({
    defaultValues: {
      qualitativeBenefits:
        formattedDatabaseData.length > 0
          ? formattedDatabaseData
          : [
              {
                category: "",
                benefits: [""],
              },
            ],
    },
  });

  // refetch the data from the database and set the form data to the database data
  useEffect(() => {
    refetch();
    if (databaseData && databaseData.qualitative_benefits.length > 0) {
      setValue(
        "qualitativeBenefits",
        databaseData?.qualitative_benefits || [
          {
            category: "",
            benefits: [""],
          },
        ]
      );
    }
  }, [databaseData, setValue, refetch]);

  // update the database when the form unmounts
  useEffect(() => {
    return () => {
      const updatedFormData = watch();
      const formattedData = updatedFormData.qualitativeBenefits.map((item) => {
        return {
          user_id: user.uuid,
          category: item.category,
          benefits: item.benefits,
        };
      });
      updateQualitativeBenefits({
        variables: {
          user_id: user.uuid,
          qualitative_benefits: formattedData,
        },
      });
    };
  }, []); // keep the [] here, so useEffect only runs on mount and unmount

  const handleNext = () => {
    navigate(`/Assumptions/${user.uuid}`);
  };

  const handlePrevious = () => {
    navigate(`/Quantitative/${user.uuid}`);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "qualitativeBenefits",
  });

  // Functions to handle adding and removing cards and benefits
  const maxCategories = 5;
  const maxBenefits = 5;
  const handleAddBenefit = (index) => {
    if (fields[index].benefits.length < maxBenefits) {
      const currentBenefits = [...fields[index].benefits];
      currentBenefits.push("");
      const updatedFields = [...fields];
      updatedFields[index].benefits = currentBenefits;
      setValue("qualitativeBenefits", updatedFields);
    }
  };

  const handleRemoveBenefit = (itemIndex, benefitIndex) => {
    if (fields[itemIndex].benefits.length > 1) {
      const updatedFields = [...fields];
      updatedFields[itemIndex].benefits.splice(benefitIndex, 1);
      setValue("qualitativeBenefits", updatedFields);
    }
  };

  // store the category and benefit data in the form data
  const handleCategoryBlur = (event, index) => {
    fields[index].category = event.target.value;
  };

  const handleBenefitBlur = (event, index, benefitIndex) => {
    fields[index].benefits[benefitIndex] = event.target.value;
  };

  return (
    <>
      <form className="mt-6 mb-20 flex flex-col space-y-4">
        {/* Benefit List Card */}
        {fields.map((item, index) => {
          return (
            <div
              key={item.id}
              className="p-6 flex flex-col space-y-4 rounded-md bg-white drop-shadow-lg relative"
            >
              <label className="text-2xl font-bold">
                {QualitativeText.card.label} {index + 1}
              </label>
              {/* Category */}
              <div className="flex">
                <label className="text-xl font-semibold">
                  {QualitativeText.category.label}
                </label>
                <Tooltip
                  title={
                    <>
                      <h3 className="font-bold">
                        {QualitativeText.category.label}
                      </h3>
                      <div>{QualitativeText.category.tooltip}</div>
                    </>
                  }
                  placement="right"
                  arrow
                >
                  <Button>
                    <CiCircleInfo className="ml-2 self-center" />
                  </Button>
                </Tooltip>
              </div>
              {/* Renders the trashcan icon to remove benefit list if you have more than one*/}
              {fields.length === 1 ? null : (
                <div className={"block absolute top-1 right-3"}>
                  <Tooltip
                    title={QualitativeText.card.removeTooltip}
                    placement="left"
                    arrow
                  >
                    <Button
                      className="text-UDig-orange"
                      onClick={() => remove(index)}
                    >
                      <FaTrash className="ml-2" />
                    </Button>
                  </Tooltip>
                </div>
              )}
              {/* Category input */}
              <input
                key={index}
                className="w-full tablet:w-1/2 h-10 px-2 border border-light-gray rounded-md"
                defaultValue={item.category}
                {...register(`qualitativeBenefits.${index}.category`, {
                  onBlur: (e) => {
                    handleCategoryBlur(e, index);
                  },
                })}
                placeholder={
                  index < QualitativeText.category.dropdown.length
                    ? `${QualitativeText.category.dropdown[index]}`
                    : ""
                }
              />
              {/* Benefits Label */}
              <div className="pt-2 flex">
                <label className="text-xl font-semibold">
                  {QualitativeText.benefits.label}
                </label>
                <Tooltip
                  title={
                    <>
                      <h3 className="font-bold">
                        {QualitativeText.benefits.label}
                      </h3>
                      <div>{QualitativeText.benefits.tooltip}</div>
                    </>
                  }
                  placement="right"
                  arrow
                >
                  <Button>
                    <CiCircleInfo className="ml-2 self-center" />
                  </Button>
                </Tooltip>
              </div>
              {/* Benefits input */}
              <>
                {item.benefits.map((benefit, benefitIndex) => (
                  <>
                    <div key={benefitIndex} className="flex">
                      <input
                        className="w-full tablet:w-1/2 h-10 px-2 border border-light-gray rounded-md"
                        {...register(
                          `qualitativeBenefits.${index}.benefits.${benefitIndex}`,
                          {
                            onBlur: (e) => {
                              handleBenefitBlur(e, index, benefitIndex);
                            },
                          }
                        )}
                        defaultValue={benefit}
                      />
                      {/* Renders the X icon to remove benefit if you have more than one*/}
                      {item.benefits.length === 1 ? null : (
                        <div className="block flex items-center">
                          <Tooltip
                            title={QualitativeText.benefits.removeTooltip}
                            placement="right"
                            arrow
                          >
                            <Button
                              className="flex items-center text-UDig-orange"
                              onClick={() =>
                                handleRemoveBenefit(index, benefitIndex)
                              }
                            >
                              <MdClose className="ml-2 mr-1" />
                            </Button>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </>
                ))}
              </>
              {/* Add benefit button. Only appears if you have 5 or less benefits. */}
              {item.benefits.length >= 5 ? null : (
                <div>
                  <Tooltip
                    title={QualitativeText.benefits.addBenefit.tooltip}
                    placement="right"
                    arrow
                  >
                    <Button
                      className="flex flex-nowrap items-center text-UDig-orange font-semibold"
                      onClick={() => handleAddBenefit(index)}
                    >
                      {QualitativeText.benefits.addBenefit.label}
                      <MdAddCircleOutline className="ml-1" />
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
        {/* Navigation and new benefit list buttons */}
        <div className="flex justify-between text-UDig-orange">
          <Button
            className="flex items-center gap-x-2"
            type="button"
            onClick={() => {
              handlePrevious();
            }}
          >
            <BsArrowLeft />
            {NavigationText.previous}
          </Button>
          {/* Add new benefit list button. Only appears if you have 5 or less benefit lists. */}
          {fields.length < maxCategories ? (
            <div>
              <Tooltip
                title={QualitativeText.card.addCard.tooltip}
                placement="top"
                arrow
              >
                <Button
                  className="flex font-semibold items-center"
                  onClick={() =>
                    append({
                      category: "",
                      benefits: [""],
                    })
                  }
                >
                  {QualitativeText.card.addCard.label}
                  <MdAddCircle className="ml-1" />
                </Button>
              </Tooltip>
            </div>
          ) : null}
          <Button
            className="flex items-center gap-x-2"
            type="button"
            onClick={() => handleNext()}
          >
            {NavigationText.next}
            <BsArrowRight />
          </Button>
        </div>
      </form>
    </>
  );
};

export default QualitativeForm;
